// frontend/src/pages/AdminPosts.tsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
// Use the full API base that already includes "/api"
const API = API_URL;

type AdminPost = {
  id: string;
  title: string;
  caption: string;
  imageUrl?: string;
  tags?: string[];
  location?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  author?: { id: string; username?: string; email?: string; avatar?: string } | null;
  likesCount?: number;
  commentsCount?: number;
};

export default function AdminPosts() {
  const [posts, setPosts] = useState<AdminPost[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  async function load() {
    try {
      setLoading(true); setErr(null);
      const res = await axios.get(`${API}/admin/posts`, { params: { page, search }, headers });
      setPosts(res.data?.posts || []);
      setTotalPages(res.data?.totalPages || 1);
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page]);

  async function onDelete(id: string) {
    if (!confirm('Delete this post? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/posts/${id}`, { headers });
      setPosts((p) => p.filter((x) => x.id !== id));
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  }

  // Editing disabled on admin posts page per request.

  async function onCreate(newPost: Partial<AdminPost>) {
    try {
      const res = await axios.post(`${API}/admin/posts`, { ...newPost }, { headers });
      const created: AdminPost = {
        id: String(res.data?._id),
        title: res.data?.title || '',
        caption: res.data?.caption || '',
        imageUrl: res.data?.imageUrl || '',
        tags: res.data?.tags || [],
        location: res.data?.location || '',
        author: res.data?.author ? { id: String(res.data.author?._id), username: res.data.author?.username, email: res.data.author?.email, avatar: res.data.author?.avatar } : null,
        createdAt: res.data?.createdAt,
        updatedAt: res.data?.updatedAt,
        likesCount: Array.isArray(res.data?.likes) ? res.data.likes.length : 0,
        commentsCount: Array.isArray(res.data?.comments) ? res.data.comments.length : 0,
      };
      setPosts((list) => [created, ...list]);
      setCreating(false);
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Create failed');
    }
  }

  const filtered = useMemo(() => {
    if (!search) return posts;
    const s = search.toLowerCase();
    return posts.filter((p) =>
      (p.title || '').toLowerCase().includes(s) ||
      (p.caption || '').toLowerCase().includes(s) ||
      (p.location || '').toLowerCase().includes(s) ||
      (p.tags || []).some((t) => t.toLowerCase().includes(s)) ||
      (p.author?.username || '').toLowerCase().includes(s)
    );
  }, [posts, search]);

  return (
    <div className="p-6 space-y-6 themed-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-heading">Admin — Posts</h2>
        <div className="flex items-center gap-2">
          <button className="btn text-xs" onClick={load}>Refresh</button>
          <button className="btn text-xs" onClick={() => setCreating((v) => !v)}>{creating ? 'Cancel' : 'New Post'}</button>
        </div>
      </div>
      {err && <div className="text-red-500 text-sm">{err}</div>}

      {/* Create post */}
      {creating && (
        <section className="p-4 rounded-xl themed-card">
          <h3 className="text-sm font-semibold text-heading">Create Post</h3>
          <CreateForm onCreate={onCreate} />
        </section>
      )}

      {/* Controls */}
      <section className="p-4 rounded-xl themed-card">
        <div className="flex flex-wrap items-center gap-2">
          <input className="input px-2 py-1 text-xs rounded-md w-64" placeholder="Search title/caption/tags/author" value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="ml-auto flex items-center gap-2">
            <button className="btn text-xs" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
            <span className="text-xs text-theme-secondary">Page {page} / {totalPages}</span>
            <button className="btn text-xs" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
          </div>
        </div>
      </section>

      {/* Posts table */}
      <section className="p-4 rounded-xl themed-card">
        <div className="mt-2 max-h-96 overflow-y-auto pr-1">
          <table className="w-full text-xs text-heading">
            <thead>
              <tr className="text-heading sticky top-0 z-10" style={{ background: 'var(--card)', boxShadow: '0 1px 0 var(--border)' }}>
                <th className="text-left px-3 py-2">Photo</th>
                <th className="text-left px-3 py-2">Title / Caption</th>
                <th className="text-left px-3 py-2">Author</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">Likes</th>
                <th className="text-left px-3 py-2">Comments</th>
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-3 py-2">Loading…</td></tr>
              ) : (
                filtered.map((p) => (
                  <tr key={p.id} className="border-t hover:opacity-95 transition odd:bg-gray-50 dark:odd:bg-gray-800" style={{ borderColor: 'var(--border)' }}>
                    <td className="px-3 py-2">
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt="post" className="w-16 h-16 rounded-md object-cover" />
                      ) : (
                        <div className="w-16 h-16 rounded-md border flex items-center justify-center text-[11px] opacity-70" style={{ borderColor: 'var(--border)' }}>(no photo)</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{p.title || '(no title)'}</div>
                      <div className="opacity-70">{(p.caption || '').slice(0, 140)}</div>
                      {(p.tags || []).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(p.tags || []).slice(0, 4).map((t, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full text-[10px] border bg-blue-500/10 text-blue-400" style={{ borderColor: 'var(--border)' }}>#{t}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {p.author?.avatar ? (
                          <img src={p.author.avatar} alt="avatar" className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <div className="w-6 h-6 rounded-full border" style={{ borderColor: 'var(--border)' }} />
                        )}
                        <span className="text-theme-secondary">{p.author?.username || '—'}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">{p.createdAt ? new Date(p.createdAt as any).toLocaleString() : '—'}</td>
                    <td className="px-3 py-2">{p.likesCount ?? 0}</td>
                    <td className="px-3 py-2">{p.commentsCount ?? 0}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button className="px-3 py-1 rounded-lg border text-xs" style={{ borderColor: 'var(--border)', color: 'var(--danger, #ef4444)' }} onClick={() => onDelete(p.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Editor removed */}
    </div>
  );
}

// EditForm removed

function CreateForm({ onCreate }: { onCreate: (p: Partial<AdminPost>) => void }) {
  const [title, setTitle] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [location, setLocation] = useState('');
  const [tags, setTags] = useState<string>('');
  const [caption, setCaption] = useState('');
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input className="input px-3 py-2 text-sm rounded-md" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <input className="input px-3 py-2 text-sm rounded-md" placeholder="Image URL" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
        <input className="input px-3 py-2 text-sm rounded-md" placeholder="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
        <input className="input px-3 py-2 text-sm rounded-md" placeholder="Tags (comma separated)" value={tags} onChange={(e) => setTags(e.target.value)} />
      </div>
      <textarea className="input px-3 py-2 text-sm rounded-md w-full" rows={4} placeholder="Caption" value={caption} onChange={(e) => setCaption(e.target.value)} />
      <div className="flex items-center gap-2">
        <button className="btn text-xs" onClick={() => onCreate({ title, imageUrl, location, tags: tags.split(',').map((t) => t.trim()).filter(Boolean), caption })}>Create</button>
      </div>
    </div>
  );
}
